import app from "@/app";
import { Controller } from "@hotwired/stimulus";

/**
 * The Stimulus JS app has a built-in method for getting a controller by element and identifier.
 * However, it does not work if the controller has not yet been initialized, 
 * and there is no guarauntee that child-controllers will be initialized prior to their parents.
 * This function solves that issue by returning a promise that resolves when the controller connects,
 * or immediately with the controller if it has already been initialized.
 * The built-in method also requires the calling class to know the controller's identifier.
 * However, this is not always possible in more dynamic settings, 
 * for instance when working with abstract form controls, which may have different identifiers.
 * This function solves that issue by using arbitrary tag, which the controller must reference when dispatching the 'tag:connect' event.
 */
export class ControllerTag<C extends Controller> {

    private connectedControllers: WeakMap<Element, C> = new WeakMap;

    constructor(public readonly tag: string) {};

    private get eventName() {
        return `${this.tag}:connect`;
    }

    public add(controller: C) {
        let el = controller.element;
        this.connectedControllers.set(el, controller);
        el.dispatchEvent(new Event(this.eventName));
    }

    // Technically not needed, since the WeakMap will eventually remove the controller, but helps to keep things clean.
    public remove(controller: C) {
        this.connectedControllers.delete(controller.element);
    }

    public get<S extends C>(el: Element): Promise<S> {
        return new Promise(resolve => {
            let controller = this.connectedControllers.get(el);
            if (controller) {
                resolve(controller as S);
                return;
            } 
            // Wait for controller to connect
            el.addEventListener(this.eventName, () => {
                resolve(this.connectedControllers.get(el) as S)
            }, { once: true });
            // Warn if it takes too long
            if (app.debug) {
                setTimeout(() => {
                    if (this.connectedControllers.get(el) == null) {
                        console.warn(`${this.tag} took too long to connect:`, el);
                    }
                }, 1000);
            }
        });
    }

    public getAll(els: Iterable<Element>): Promise<C[]> {
        return Promise.all(Array.from(els).map(el => this.get(el)));
    }
}
